import {Injectable, OnDestroy} from '@angular/core';
import {
  AcaoPendenteMiaGestor,
  AnaliseCardapioMiaGestor,
  ChecklistMiaGestor,
  MiaGestorService,
  ProgressoAnaliseMiaGestor,
  ProvisionamentoEmpresaMiaGestor,
  RespostaRetomadaMiaGestor,
  RespostaTurnoMiaGestor,
  SnapshotMiaGestor,
} from './mia-gestor.service';

export type PapelMensagemGestor = 'usuario' | 'gestor' | 'nota';

export interface TrechoMensagemGestor {
  texto: string;
  destaque: boolean;
  /** href absoluto quando o trecho é uma URL — o template renderiza como âncora */
  link?: string;
}

/**
 * Quebra o texto nos destaques de negrito. O padrão é o do WhatsApp — UM
 * asterisco — mas o ** duplo ainda é aceito por causa de conversas antigas
 * gravadas antes da regra de formatação no prompt. Depois do negrito, cada
 * trecho ainda é quebrado nas URLs, porque o WhatsApp linka endereço no
 * meio da frase.
 */
export function trechosDaMensagem(texto: string): TrechoMensagemGestor[] {
  const mensagem = String(texto || '');
  const trechos: TrechoMensagemGestor[] = [];
  const destaque = /\*\*([^*\n]+?)\*\*|\*([^*\n]+?)\*/g;
  let inicio = 0;
  let encontrado: RegExpExecArray | null;

  while ((encontrado = destaque.exec(mensagem)) !== null) {
    if (encontrado.index > inicio) {
      trechos.push({texto: mensagem.slice(inicio, encontrado.index), destaque: false});
    }
    trechos.push({texto: encontrado[1] !== undefined ? encontrado[1] : encontrado[2], destaque: true});
    inicio = encontrado.index + encontrado[0].length;
  }
  if (inicio < mensagem.length || !trechos.length) {
    trechos.push({texto: mensagem.slice(inicio), destaque: false});
  }
  return trechos.reduce(
    (todos, trecho) => todos.concat(separeLinks(trecho)),
    [] as TrechoMensagemGestor[],
  );
}

/** quebra um trecho já classificado nas URLs, preservando o negrito */
function separeLinks(trecho: TrechoMensagemGestor): TrechoMensagemGestor[] {
  const url = /(?:https?:\/\/|www\.)[^\s]+/gi;
  const partes: TrechoMensagemGestor[] = [];
  let inicio = 0;
  let encontrado: RegExpExecArray | null;

  while ((encontrado = url.exec(trecho.texto)) !== null) {
    // pontuação no fim é da frase, não do endereço: "veja https://x.com." linka sem o ponto
    const endereco = encontrado[0].replace(/[.,;:!?)\]}»"']+$/, '');
    if (!endereco || endereco.toLowerCase() === 'www') continue;
    if (encontrado.index > inicio) {
      partes.push({texto: trecho.texto.slice(inicio, encontrado.index), destaque: trecho.destaque});
    }
    partes.push({
      texto: endereco,
      destaque: trecho.destaque,
      link: /^https?:\/\//i.test(endereco) ? endereco : 'https://' + endereco,
    });
    inicio = encontrado.index + endereco.length;
  }
  if (inicio < trecho.texto.length || !partes.length) {
    partes.push({texto: trecho.texto.slice(inicio), destaque: trecho.destaque});
  }
  return partes;
}

/** a Cloud API corta o rótulo do botão aqui — o painel corta no mesmo lugar */
export const LIMITE_ROTULO = 20;

export interface BotaoGestor {
  rotulo: string;
  /**
   * 'texto' é o reply button puro: tocar envia o rótulo como mensagem, que é o
   * que o WhatsApp faz. As outras são o que o painel executa no lugar da página
   * assinada que o cta_url abriria no celular.
   */
  acao: 'texto' | 'aprovar' | 'cancelar' | 'importar' | 'revisar' | 'abrir';
  /** só para 'abrir' */
  href?: string;
}

/** produto como vem no resumo do Servidor (analise.categorias[].produtos[]) */
export interface ProdutoResumoGestor {
  referencia: string;
  nome: string | null;
  preco: number | null;
  selecionado: boolean;
  tipoProduto?: 'NORMAL' | 'PIZZA';
  tamanhos?: Array<{nome: string | null; preco: number | null}>;
  bloqueios?: string[];
}

export interface ItemListaGestor {
  titulo: string;
  descricao: string;
}

/**
 * UM interativo por mensagem, porque é assim no canal: a Cloud API aceita
 * `interactive` do tipo button, list OU cta_url — nunca dois na mesma bolha. Se
 * um turno precisar de botão de resposta e de link, são duas mensagens.
 */
export interface InterativoGestor {
  tipo: 'botoes' | 'lista' | 'link';
  /** 'botoes': até 3 · 'link': exatamente 1, o cta_url */
  botoes?: BotaoGestor[];
  /** 'lista': rótulo do botão que abre a folha */
  rotulo?: string;
  /** 'lista': até 10 itens */
  itens?: ItemListaGestor[];
}

export interface MensagemGestor {
  papel: PapelMensagemGestor;
  texto: string;
  horario: Date;
  interativo?: InterativoGestor;
  ferramentas?: Array<{nome: string; sucesso: boolean; duracaoMs: number}>;
  /**
   * anexo enviado pelo usuário. PDF vira cartão de documento, como no WhatsApp;
   * foto vira preview — `imagens` são object URLs locais deste navegador, válidas
   * só nesta sessão (o histórico que volta do servidor não tem bolha de anexo).
   */
  documento?: {nome: string; tamanhoBytes: number; quantidade: number; imagens?: string[]};
}

/**
 * Botões de resposta que a Mia mandou no turno. Aplica os dois limites do canal
 * — até 3 botões, rótulo cortado em 20 — para o painel não prometer o que o
 * WhatsApp não entrega.
 */
export function botoesDeResposta(rotulos: string[]): InterativoGestor | undefined {
  const botoes: BotaoGestor[] = (rotulos || [])
    .slice(0, 3)
    .map(rotulo => ({rotulo: String(rotulo || '').slice(0, LIMITE_ROTULO), acao: 'texto' as const}))
    .filter(botao => !!botao.rotulo);
  return botoes.length ? {tipo: 'botoes', botoes} : undefined;
}

/**
 * Interativo da resposta da Mia. O link ([ABRIR]) vira o cta_url e tem
 * precedência: o canal aceita UM tipo por mensagem, e o prompt manda escrever
 * o endereço no texto quando também precisa de botões de resposta.
 */
export function interativoDeResposta(
  rotulos: string[],
  link?: {rotulo: string; url: string} | null
): InterativoGestor | undefined {
  if (link && link.url) {
    return {
      tipo: 'link',
      botoes: [{
        rotulo: String(link.rotulo || 'Abrir').slice(0, LIMITE_ROTULO),
        acao: 'abrir',
        href: link.url,
      }],
    };
  }
  return botoesDeResposta(rotulos);
}

/**
 * Estado da conversa do Gestor, compartilhado pelo painel e pelo simulador.
 * As duas telas renderizam a MESMA conversa: mesmo onboarding, mesma máquina de
 * estados, mesmo ledger de ações. O que muda é só a casca.
 */
@Injectable()
export class ConversaGestorStore implements OnDestroy {
  mensagens: MensagemGestor[] = [];
  /** object URLs dos previews de foto, liberadas quando a conversa é trocada ou a tela fecha */
  private urlsDeImagem: string[] = [];

  onboardingId: number | null = null;
  conversaId: number | null = null;
  empresaId: number | null = null;
  etapa = 'CARREGANDO';
  status = 'ATIVO';
  versao = 0;
  checklist: ChecklistMiaGestor = {
    empresa: 'PENDENTE',
    cardapio: 'PENDENTE',
    formasEntrega: 'PENDENTE',
    link: 'PENDENTE',
  };
  acaoPendente: AcaoPendenteMiaGestor | null = null;
  provisionamentoEmpresa: ProvisionamentoEmpresaMiaGestor | null = null;
  analiseCardapio: AnaliseCardapioMiaGestor | null = null;

  carregando = true;
  enviando = false;
  operandoAcao = false;
  operandoCardapio = false;
  /** contagem real de produtos/categorias enquanto o extrator lê o cardápio */
  progressoAnalise: ProgressoAnaliseMiaGestor | null = null;
  reiniciando = false;
  agenteDisponivel = false;
  verificandoAgente = true;
  erro = '';

  private acompanhamentoAnalise: any = null;
  /** bolha derivada do estado, e a lista já concatenada — ver linhaDoTempo */
  private pendencia: MensagemGestor | null = null;
  private chaveMontada = '';
  private linha: MensagemGestor[] = [];
  private linhaBase: MensagemGestor[] | null = null;

  constructor(private servico: MiaGestorService) {}

  inicie(): Promise<void> {
    this.verifiqueAgente();
    return this.crieOuRetome();
  }

  verifiqueAgente(): Promise<void> {
    this.verificandoAgente = true;
    return this.servico.obtenhaSaude()
      .then(resposta => this.agenteDisponivel = !!(resposta && resposta.ok === true))
      .catch(() => this.agenteDisponivel = false)
      .then(() => {
        this.verificandoAgente = false;
      });
  }

  crieOuRetome(): Promise<void> {
    this.carregando = true;
    this.erro = '';
    return this.servico.crieOuRetome()
      .then((resposta: RespostaRetomadaMiaGestor) => {
        this.apliqueSnapshot(resposta);
        this.conversaId = resposta.conversaId || null;
        this.libereImagens();
        this.mensagens = (resposta.mensagens || []).map(item => ({
          papel: this.papelDe(item.papel),
          texto: item.texto,
          horario: item.criadoEm ? new Date(item.criadoEm) : new Date(),
          interativo: interativoDeResposta(item.botoes || [], item.link),
        }));
      })
      .catch(erro => {
        this.erro = this.textoDoErro(erro, 'Não foi possível carregar a conversa.');
      })
      .then(() => {
        this.carregando = false;
      });
  }

  envie(texto: string): Promise<void> {
    const mensagem = String(texto || '').trim();
    if (!mensagem || this.enviando || !this.onboardingId) return Promise.resolve();

    this.mensagens.push({papel: 'usuario', texto: mensagem, horario: new Date()});
    this.enviando = true;

    return this.servico.envieTurno(this.onboardingId, mensagem)
      .then((resposta: RespostaTurnoMiaGestor) => {
        this.agenteDisponivel = true;
        this.conversaId = resposta.conversaId || this.conversaId;
        this.apliqueSnapshot(resposta);
        this.mensagens.push({
          papel: 'gestor',
          texto: resposta.texto,
          horario: new Date(),
          interativo: interativoDeResposta(resposta.botoes || [], resposta.link),
          ferramentas: resposta.ferramentas || [],
        });
      })
      .catch(erro => {
        this.agenteDisponivel = false;
        this.erro = this.textoDoErro(erro, 'O serviço não respondeu.');
        this.anote('A resposta da Mia não foi confirmada. Recarregue a conversa para verificar o que ficou salvo.');
      })
      .then(() => {
        this.enviando = false;
      });
  }

  aprove(): Promise<void> {
    if (!this.acaoPendente || this.operandoAcao || !this.onboardingId) return Promise.resolve();
    this.operandoAcao = true;
    this.erro = '';
    return this.servico.aproveAcao(this.acaoPendente, this.versao)
      .then(resposta => {
        const resultado = resposta && resposta.resultado;
        const anexoCardapioColado = resultado && resultado.cardapioColado &&
          resultado.cardapioColado.anexo;
        if (resultado && resultado.empresa) this.provisionamentoEmpresa = resultado;
        // a Mia apresenta o resultado e já puxa a próxima etapa; o anúncio
        // determinístico fica de reserva para quando o agente não respondeu
        const miaFalou = this.apresenteTurnoDaMia(resposta && resposta.turnoMia);
        if (!miaFalou && resultado && resultado.empresa) {
          this.anuncieAEmpresa(!!anexoCardapioColado);
        }
        if (!miaFalou && resultado && resultado.analiseId) {
          this.anuncieAImportacao(resultado);
        }
        return this.recarregue().then(() => {
          // cardápio colado antes da empresa: o anexo convertido na aprovação
          // entra em análise agora, sem depender de outra mensagem do usuário
          if (anexoCardapioColado) this.analiseCardapioConvertido([anexoCardapioColado.id]);
        });
      })
      .catch(erro => {
        const mensagem = this.textoDoErro(erro, 'Não foi possível aprovar a ação.');
        return this.recarregue().catch(() => undefined).then(() => {
          this.erro = mensagem;
          this.anote(mensagem + ' Confira o estado atualizado antes de tentar novamente.');
        });
      })
      .then(() => {
        this.operandoAcao = false;
      });
  }

  cancele(): Promise<void> {
    if (!this.acaoPendente || this.operandoAcao) return Promise.resolve();
    this.operandoAcao = true;
    this.erro = '';
    const acaoId = this.acaoPendente.id;
    return this.servico.canceleAcao(acaoId)
      .then(() => {
        this.anote('Proposta cancelada. Nada foi executado.');
        return this.recarregue();
      })
      .catch(erro => {
        const mensagem = this.textoDoErro(erro, 'Não foi possível cancelar a proposta.');
        return this.recarregue().catch(() => undefined).then(() => this.erro = mensagem);
      })
      .then(() => {
        this.operandoAcao = false;
      });
  }

  anexeEAnalise(arquivos: File[]): Promise<void> {
    if (!this.onboardingId || !arquivos.length || this.operandoCardapio) return Promise.resolve();
    const problema = this.valideArquivos(arquivos);
    if (problema) {
      this.erro = problema;
      return Promise.resolve();
    }

    this.operandoCardapio = true;
    this.erro = '';
    this.inicieAcompanhamentoDaAnalise();
    this.mensagens.push({
      papel: 'usuario',
      texto: arquivos.length === 1 ? arquivos[0].name : `${arquivos.length} fotos do cardápio`,
      horario: new Date(),
      documento: {
        nome: arquivos[0].name,
        tamanhoBytes: arquivos.reduce((total, arquivo) => total + arquivo.size, 0),
        quantidade: arquivos.length,
        imagens: this.previewsDe(arquivos),
      },
    });

    return this.servico.envieAnexosCardapio(this.onboardingId, arquivos)
      .then(resposta => this.servico.analiseCardapio(
        this.onboardingId as number,
        resposta.anexos.map(anexo => anexo.id)
      ))
      .then(analise => {
        // sem push da bolha do rascunho aqui: ela é derivada do estado (linhaDoTempo).
        // Empurrada, ela viraria duas depois de um F5 — o histórico volta do
        // servidor e o estado remontaria a mesma bolha no fim. A fala da Mia,
        // essa sim, é um turno persistido: entra uma vez.
        this.analiseCardapio = analise;
        this.apresenteTurnoDaMia(analise && analise.turnoMia);
        return this.recarregue();
      })
      .catch(erro => {
        const mensagem = this.textoDoErro(erro, 'Não foi possível analisar o cardápio.');
        return this.recarregue().catch(() => undefined).then(() => {
          this.erro = mensagem;
          this.anote(mensagem);
        });
      })
      .then(() => {
        this.encerreAcompanhamentoDaAnalise();
        this.operandoCardapio = false;
      });
  }

  /** durante a análise, busca a cada 2s a contagem real que o extrator já leu */
  private inicieAcompanhamentoDaAnalise(): void {
    this.progressoAnalise = null;
    this.acompanhamentoAnalise = setInterval(() => {
      if (!this.onboardingId) return;
      this.servico.obtenhaProgressoAnalise(this.onboardingId)
        .then(resposta => {
          if (this.operandoCardapio && resposta && resposta.progresso) {
            this.progressoAnalise = resposta.progresso;
          }
        })
        .catch(() => undefined);
    }, 2000);
  }

  private encerreAcompanhamentoDaAnalise(): void {
    if (this.acompanhamentoAnalise) clearInterval(this.acompanhamentoAnalise);
    this.acompanhamentoAnalise = null;
    this.progressoAnalise = null;
  }

  /**
   * Cardápio colado antes de a empresa existir: a aprovação converteu o texto
   * guardado em anexo e a análise começa aqui, no mesmo fluxo do upload — sem
   * bolha de documento (o texto colado já é uma mensagem da conversa).
   */
  private analiseCardapioConvertido(anexoIds: number[]): Promise<void> {
    if (!this.onboardingId || !anexoIds.length || this.operandoCardapio) return Promise.resolve();
    this.operandoCardapio = true;
    this.erro = '';
    this.inicieAcompanhamentoDaAnalise();
    return this.servico.analiseCardapio(this.onboardingId as number, anexoIds)
      .then(analise => {
        this.analiseCardapio = analise;
        this.apresenteTurnoDaMia(analise && analise.turnoMia);
        return this.recarregue();
      })
      .catch(erro => {
        const mensagem = this.textoDoErro(erro, 'Não foi possível analisar o cardápio.');
        return this.recarregue().catch(() => undefined).then(() => {
          this.erro = mensagem;
          this.anote(mensagem);
        });
      })
      .then(() => {
        this.encerreAcompanhamentoDaAnalise();
        this.operandoCardapio = false;
      });
  }

  salveRascunho(): Promise<void> {
    if (!this.podeRevisarCardapio || this.operandoCardapio || !this.onboardingId) return Promise.resolve();
    this.operandoCardapio = true;
    this.erro = '';
    return this.servico.salveRascunhoCardapio(this.onboardingId, this.analiseCardapio as AnaliseCardapioMiaGestor)
      .then(analise => {
        this.analiseCardapio = analise;
        this.anote(`Revisão salva — versão ${analise.versao}.`);
      })
      .catch(erro => this.erro = this.textoDoErro(erro, 'Não foi possível salvar a revisão.'))
      .then(() => {
        this.operandoCardapio = false;
      });
  }

  prepareImportacao(): Promise<void> {
    if (!this.podeRevisarCardapio || this.operandoCardapio || !this.onboardingId) return Promise.resolve();
    // produto sem preço falharia na importação: sai da seleção antes da proposta
    this.desmarqueProdutosBloqueados();
    const referencias = this.referenciasSelecionadas;
    if (!referencias.length) {
      this.erro = this.bloqueiosRascunho
        ? 'Nenhum produto selecionado tem preço. Informe os preços na conversa ou no painel antes de importar.'
        : 'Selecione ao menos um produto para importar.';
      return Promise.resolve();
    }
    if (referencias.length > 500) {
      this.erro = 'São no máximo 500 produtos por importação. Divida em duas rodadas.';
      return Promise.resolve();
    }

    this.operandoCardapio = true;
    this.erro = '';
    return this.servico.salveRascunhoCardapio(this.onboardingId, this.analiseCardapio as AnaliseCardapioMiaGestor)
      .then(analise => {
        this.analiseCardapio = analise;
        return this.servico.proponhaImportacaoCardapio(
          this.onboardingId as number,
          analise.id,
          this.referenciasSelecionadas
        );
      })
      .then(resposta => {
        this.acaoPendente = resposta.acaoPendente;
        this.versao = resposta.versao;
        this.etapa = resposta.etapa;
        this.analiseCardapio = resposta.analise;
        // a bolha de aprovação também é derivada: quem manda nela é acaoPendente
      })
      .catch(erro => {
        const mensagem = this.textoDoErro(erro, 'Não foi possível preparar a importação.');
        return this.recarregue().catch(() => undefined).then(() => {
          this.erro = mensagem;
          this.anote(mensagem);
        });
      })
      .then(() => {
        this.operandoCardapio = false;
      });
  }

  /**
   * Encerra a implantação atual e abre outra do zero. Empresa e produtos já
   * criados continuam existindo — o que se perde é o contexto da conversa.
   */
  reinicie(): Promise<void> {
    if (!this.onboardingId || this.reiniciando) return Promise.resolve();
    this.reiniciando = true;
    this.erro = '';
    return this.servico.reinicie(this.onboardingId)
      .then((resposta: RespostaRetomadaMiaGestor) => {
        // o snapshot novo não traz esses campos, e apliqueSnapshot só sobrescreve
        // o que vem definido: sem zerar aqui, a empresa anterior continua na ficha
        this.provisionamentoEmpresa = null;
        this.analiseCardapio = null;
        this.acaoPendente = null;
        this.apliqueSnapshot(resposta);
        this.conversaId = resposta.conversaId || null;
        this.libereImagens();
        this.mensagens = (resposta.mensagens || []).map(item => ({
          papel: this.papelDe(item.papel),
          texto: item.texto,
          horario: item.criadoEm ? new Date(item.criadoEm) : new Date(),
          interativo: interativoDeResposta(item.botoes || [], item.link),
        }));
      })
      .catch(erro => {
        this.erro = this.textoDoErro(erro, 'Não foi possível começar do zero.');
      })
      .then(() => {
        this.reiniciando = false;
      });
  }

  recarregue(): Promise<void> {
    if (!this.onboardingId) return Promise.resolve();
    return this.servico.obtenhaOnboarding(this.onboardingId).then(resposta => {
      this.apliqueSnapshot(resposta);
      this.conversaId = resposta.conversaId || this.conversaId;
    });
  }

  // ----- linha do tempo ---------------------------------------------------

  /**
   * O que a tela mostra: o histórico, mais a bolha que ainda espera uma ação.
   *
   * O servidor guarda texto, não botão — depois de um F5 a proposta pendente
   * voltaria muda. Por isso ela é remontada aqui a partir do estado, e NÃO é
   * empurrada em `mensagens`: seria uma bolha no histórico e outra derivada, a
   * mesma pergunta duas vezes. O que já aconteceu (empresa criada, importação
   * concluída) é histórico de verdade e vive em `mensagens`.
   */
  get linhaDoTempo(): MensagemGestor[] {
    const chave = this.chaveDaPendencia();
    if (!chave) {
      this.pendencia = null;
      this.chaveMontada = '';
      this.linhaBase = null;
      return this.mensagens;
    }

    // cache pela chave: sem ele o getter recriaria o objeto a cada ciclo de
    // detecção, o *ngFor veria um item removido e outro inserido, e a bolha
    // renasceria — com a animação de entrada — sem parar
    if (!this.pendencia || this.chaveMontada !== chave) {
      this.pendencia = this.montePendencia();
      this.chaveMontada = chave;
      this.linhaBase = null;
    }

    // a lista concatenada também é guardada, para o getter não alocar um array
    // a cada ciclo só para devolver o mesmo conteúdo
    if (this.linhaBase !== this.mensagens || this.linha.length !== this.mensagens.length + 1) {
      this.linhaBase = this.mensagens;
      this.linha = this.mensagens.concat([this.pendencia]);
    }
    return this.linha;
  }

  private chaveDaPendencia(): string {
    if (this.acaoPendente) return `acao:${this.acaoPendente.id}:${this.versao}`;
    if (this.podeRevisarCardapio) {
      const analise = this.analiseCardapio as AnaliseCardapioMiaGestor;
      return `rascunho:${analise.id}:${analise.versao}`;
    }
    return '';
  }

  private montePendencia(): MensagemGestor {
    return this.acaoPendente ? this.monteAAprovacao() : this.monteORascunho();
  }

  /** aprovação de risco: resumo congelado no texto e dois botões de resposta */
  private monteAAprovacao(): MensagemGestor {
    const acao = this.acaoPendente as AcaoPendenteMiaGestor;
    const linhas = [`*${this.tituloDaAcao}*`, acao.impacto]
      .concat(this.linhasDoResumo())
      .filter(linha => !!linha);
    return {
      papel: 'gestor',
      horario: new Date(),
      texto: linhas.join('\n'),
      interativo: {
        tipo: 'botoes',
        botoes: [
          {rotulo: this.rotuloDaAprovacao.slice(0, LIMITE_ROTULO), acao: 'aprovar'},
          {rotulo: 'Cancelar', acao: 'cancelar'},
        ],
      },
    };
  }

  /**
   * Cardápio lido: o resumo cabe na bolha, o detalhe fica atrás do botão. O que
   * muda a bolha é o preço: sem nenhum, não existe "Importar tudo" — existe pedir
   * os preços aqui mesmo, que é o caminho mais curto para o lojista.
   */
  private monteORascunho(): MensagemGestor {
    const analise = this.analiseCardapio as AnaliseCardapioMiaGestor;
    const categorias = Number((analise.estatisticas && analise.estatisticas.categorias) || 0);
    const total = this.totalProdutosRascunho;
    const bloqueados = this.produtosBloqueadosRascunho;
    const prontos = Math.max(0, total - bloqueados.length);
    const cabecalho = `Li seu cardápio e achei *${total} itens* em *${categorias} categorias*.`;
    const revisar = {rotulo: 'Quero revisar', acao: 'revisar' as const};

    if (!bloqueados.length) {
      return this.bolhaDoGestor(`Prontinho! 🎉 ${cabecalho} Está tudo pronto para importar.`, [
        {rotulo: 'Importar tudo', acao: 'importar'},
        revisar,
      ]);
    }
    if (!prontos) {
      return this.bolhaDoGestor(
        `${cabecalho}\n\nSó que *nenhum veio com preço* 🙈 ${this.dicaParaOsPrecos(bloqueados)}`,
        [revisar]
      );
    }
    return this.bolhaDoGestor(
      `Prontinho! 🎉 ${cabecalho}\n\n*${prontos} já com preço* e *${bloqueados.length} sem preço* ` +
        `(${this.nomesResumidos(bloqueados)}). ${this.dicaParaOsPrecos(bloqueados)}`,
      [
        {rotulo: `Importar ${prontos} prontos`.slice(0, LIMITE_ROTULO), acao: 'importar'},
        revisar,
      ]
    );
  }

  private bolhaDoGestor(texto: string, botoes: BotaoGestor[]): MensagemGestor {
    return {papel: 'gestor', horario: new Date(), texto, interativo: {tipo: 'botoes', botoes}};
  }

  /** o jeito mais curto de resolver: mandar os preços na conversa, no formato que a Mia lê */
  private dicaParaOsPrecos(bloqueados: ProdutoResumoGestor[]): string {
    const semPreco = bloqueados.filter(produto => this.bloqueioEhDePreco(produto));
    // o que não é preço (categoria sem nome, item sem nome) se resolve no painel
    const outrosMotivos = new Map<string, number>();
    bloqueados.forEach(produto => (produto.bloqueios || [])
      .filter(motivo => !/sem preço/i.test(motivo))
      .forEach(motivo => outrosMotivos.set(motivo, (outrosMotivos.get(motivo) || 0) + 1)));
    const temPizza = semPreco.some(produto => produto.tipoProduto === 'PIZZA');
    const temNormal = semPreco.some(produto => produto.tipoProduto !== 'PIZZA');
    let dica = '';
    if (temPizza && temNormal) {
      dica = 'Me manda os preços aqui mesmo que eu preencho tudo: a tabela de tamanhos das pizzas ' +
        '(ex.: _P 30, M 40, G 50_) e os demais itens um por linha, nome e valor — ou ajuste no painel.';
    } else if (temPizza) {
      dica = 'Me manda a tabela de tamanhos aqui mesmo (ex.: _P 30, M 40, G 50_) que eu preencho ' +
        'todas as pizzas — ou ajuste no painel.';
    } else if (temNormal) {
      dica = 'Me manda os preços aqui mesmo, um item por linha com nome e valor, que eu preencho tudo — ' +
        'ou ajuste no painel.';
    }
    if (outrosMotivos.size) {
      const lista = Array.from(outrosMotivos.entries())
        .map(([motivo, quantidade]) => `${motivo} (${quantidade === 1 ? '1 item' : `${quantidade} itens`})`)
        .join(', ');
      dica += `${dica ? ' ' : ''}Também precisa de ajuste no painel: ${lista}.`;
    }
    return dica;
  }

  private nomesResumidos(produtos: ProdutoResumoGestor[]): string {
    const nomes = produtos.slice(0, 3).map(produto => produto.nome || produto.referencia);
    const restantes = produtos.length - nomes.length;
    return nomes.join(', ') + (restantes > 0 ? ` e mais ${restantes}` : '');
  }

  private bloqueioEhDePreco(produto: ProdutoResumoGestor): boolean {
    return (produto.bloqueios || []).some(motivo => /sem preço/i.test(motivo));
  }

  /**
   * O resumo da proposta em linhas curtas. Hash e expiração ficam de fora de
   * propósito: no WhatsApp não caberiam, e no painel eles moram na ficha.
   */
  private linhasDoResumo(): string[] {
    const acao = this.acaoPendente;
    if (!acao) return [];

    if (acao.tipo === 'IMPORTAR_CARDAPIO') {
      const selecionados = this.referenciasSelecionadas.length;
      return selecionados ? [`${selecionados} item(ns) selecionado(s).`] : [];
    }

    const empresa = this.previewEmpresa;
    if (!empresa) return [];
    const linhas = [`${empresa.nome} · ${empresa.subdominio}`];
    const contato = [empresa.responsavel && empresa.responsavel.nome, empresa.whatsapp]
      .filter(parte => !!parte).join(' · ');
    if (contato) linhas.push(contato);
    if (empresa.endereco) linhas.push(String(empresa.endereco));
    return linhas;
  }

  // ----- o que já aconteceu vira bolha, não cartão ------------------------

  /** empresa criada: texto curto e um cta_url, que é o que o canal daria */
  private anuncieAEmpresa(cardapioColado: boolean): void {
    const provisionamento = this.provisionamentoEmpresa;
    if (!provisionamento) return;
    const endereco = this.enderecoDaLoja;
    this.mensagens.push({
      papel: 'gestor',
      horario: new Date(),
      texto: `Pronto! A *${provisionamento.empresa.nome}* já existe` +
        (endereco
          ? `, e o endereço dela é ${endereco.hostname}.`
          : '. Os endereços dela entram no ar quando a loja for para produção.') +
        // cardápio colado antes de a loja existir foi guardado no onboarding e
        // virou anexo na aprovação — nunca pedir de novo o que já foi enviado
        (cardapioColado
          ? '\nO cardápio que você colou está comigo — já comecei a leitura e te aviso aqui quando terminar.'
          : '\nAgora me manda o cardápio: um PDF de até 10 MB, até 10 fotos — ou cola o texto aqui na conversa.'),
      interativo: endereco
        ? {tipo: 'link', botoes: [{rotulo: 'Abrir minha loja', acao: 'abrir', href: endereco.url}]}
        : undefined,
    });
  }

  /** importação: os números e o MOTIVO no texto, as falhas atrás de uma lista */
  private anuncieAImportacao(resultado: any): void {
    const importados = (resultado.importados || []).length;
    const reutilizados = (resultado.reutilizados || []).length;
    const falhas = resultado.falhas || [];
    const conciliar = resultado.requerConciliacao || [];
    const entraram = importados + reutilizados;

    const itens: ItemListaGestor[] = falhas
      .map((falha: any) => ({
        titulo: String(falha.nome || falha.referencia),
        descricao: String(falha.mensagem || ''),
      }))
      .concat(conciliar.map((referencia: any) => ({
        titulo: String(referencia),
        descricao: 'conferir manualmente antes de repetir',
      })))
      .slice(0, 10);

    const total = falhas.length + conciliar.length;
    const conferencia = conciliar.length
      ? ` ${conciliar.length === 1 ? '1 precisa' : `${conciliar.length} precisam`} de conferência.`
      : '';
    const dica = falhas.some((falha: any) => /sem preço/i.test(String(falha.mensagem || '')))
      ? 'Me manda os preços que faltam aqui mesmo, que eu preencho e importo o resto.'
      : 'Veja a lista e ajuste no painel antes de tentar de novo.';
    let texto: string;
    if (resultado.conclusao === 'CONCLUIDA') {
      texto = `Importei *${entraram} produto(s)* para o cardápio da sua loja. 🎉`;
    } else if (!entraram) {
      texto = `Nenhum produto entrou 😕 ${falhas.length === 1 ? 'O item falhou' : `Os ${falhas.length} falharam`}` +
        (falhas.length ? `: ${this.resumaOsMotivos(falhas)}.` : '.') + conferencia + `\n${dica}`;
    } else {
      texto = `Importação parcial: *${entraram} entraram* e ${falhas.length} ` +
        `${falhas.length === 1 ? 'falhou' : 'falharam'}` +
        (falhas.length ? ` (${this.resumaOsMotivos(falhas)})` : '') + `.${conferencia}\n` +
        `O que já entrou não será recriado numa nova tentativa. ${dica}`;
    }
    this.mensagens.push({
      papel: 'gestor',
      horario: new Date(),
      texto,
      interativo: itens.length
        ? {
          tipo: 'lista',
          // o rótulo cabe nos 20 do canal; acima de 10 itens a lista é cortada
          rotulo: total > 10 ? 'Ver os 10 primeiros' : 'Ver o que faltou',
          itens,
        }
        : undefined,
    });
  }

  /**
   * Primeiro endereço publicável da loja. Em desenvolvimento todas as zonas vêm
   * NAO_APLICAVEL, e aí não existe link nenhum para oferecer.
   */
  get enderecoDaLoja(): {hostname: string; url: string} | null {
    const zonas = (this.provisionamentoEmpresa && this.provisionamentoEmpresa.dns) || [];
    const zona = zonas.find(item => item.status === 'CONFIGURADO' || item.status === 'PENDENTE');
    return zona ? {hostname: zona.hostname, url: `https://${zona.hostname}`} : null;
  }

  anote(texto: string): void {
    this.mensagens.push({papel: 'nota', texto, horario: new Date()});
  }

  marqueTodosOsProdutos(selecionado: boolean): void {
    const rascunho = this.analiseCardapio && this.analiseCardapio.rascunho;
    if (!rascunho) return;
    rascunho.categorias.forEach((categoria: any) =>
      categoria.produtos.forEach((produto: any) => {
        if (!this.produtoJaImportado(produto.referencia)) produto.selecionado = selecionado;
      }));
  }

  produtoJaImportado(referencia: string): boolean {
    const itens = this.analiseCardapio && this.analiseCardapio.itens;
    return !!(itens || []).some(item => item.referencia === referencia && item.status === 'IMPORTADO');
  }

  /** "todos sem preço", "5 sem preço e 1 com outro problema" */
  private resumaOsMotivos(falhas: any[]): string {
    const semPreco = falhas.filter((falha: any) => /sem preço/i.test(String(falha.mensagem || ''))).length;
    const outros = falhas.length - semPreco;
    if (!outros) return falhas.length === 1 ? 'sem preço' : 'todos sem preço';
    if (!semPreco) return falhas.length === 1 ? 'veja o motivo na lista' : 'veja os motivos na lista';
    return `${semPreco} sem preço e ${outros} com outro problema`;
  }

  /**
   * Bloqueio calculado no rascunho local, para reagir enquanto o lojista digita:
   * produto normal sem preço; pizza sem grupo de tamanhos ou com tamanho sem preço.
   */
  produtoTemBloqueio(produto: any): boolean {
    if (produto.tipoProduto === 'PIZZA') {
      const grupo = this.grupoDeTamanhos(produto);
      return !grupo || !(grupo.opcoes || []).length ||
        (grupo.opcoes || []).some((opcao: any) => this.semValor(opcao.preco));
    }
    return this.semValor(produto.preco);
  }

  /** frase completa do aviso na página de revisão; vazia quando não há bloqueio de preço */
  motivoDoBloqueio(produto: any): string {
    if (!this.produtoTemBloqueio(produto)) return '';
    if (produto.tipoProduto !== 'PIZZA') {
      return 'sem preço no documento — informe o valor ou desmarque o item.';
    }
    const grupo = this.grupoDeTamanhos(produto);
    if (!grupo || !(grupo.opcoes || []).length) {
      return 'pizza sem tamanhos e sem preço — mande a tabela de tamanhos na conversa ' +
        '(ex.: P 30, M 40, G 50) ou desmarque o item.';
    }
    const semPreco = (grupo.opcoes || [])
      .filter((opcao: any) => this.semValor(opcao.preco))
      .map((opcao: any) => opcao.nome || 'sem nome');
    return `pizza sem preço por tamanho (${semPreco.join(', ')}) — informe o valor de cada tamanho ` +
      `em "${grupo.nome || 'Tamanho'}" ou mande a tabela na conversa.`;
  }

  grupoDeTamanhos(produto: any): any | null {
    return (produto.adicionais || []).find((adicional: any) => adicional.finalidade === 'TAMANHO_PIZZA') || null;
  }

  private semValor(preco: any): boolean {
    return preco === null || preco === undefined || preco === '';
  }

  private desmarqueProdutosBloqueados(): void {
    const rascunho = this.analiseCardapio && this.analiseCardapio.rascunho;
    if (!rascunho) return;
    rascunho.categorias.forEach((categoria: any) =>
      categoria.produtos.forEach((produto: any) => {
        if (this.produtoTemBloqueio(produto) && !this.produtoJaImportado(produto.referencia)) {
          produto.selecionado = false;
        }
      }));
  }

  /** produtos que o Servidor apontou como bloqueados, com nome, tipo e motivos */
  get produtosBloqueadosRascunho(): ProdutoResumoGestor[] {
    const categorias = (this.analiseCardapio && this.analiseCardapio.categorias) || [];
    const bloqueados: ProdutoResumoGestor[] = [];
    categorias.forEach((categoria: any) => (categoria.produtos || []).forEach((produto: any) => {
      if ((produto.bloqueios || []).length) bloqueados.push(produto);
    }));
    return bloqueados;
  }

  get podeEnviarCardapio(): boolean {
    return !!this.empresaId &&
      ['AGUARDANDO_CARDAPIO', 'REVISANDO_CARDAPIO'].indexOf(this.etapa) >= 0 &&
      !this.acaoPendente &&
      !(this.analiseCardapio && this.analiseCardapio.rascunho);
  }

  get podeRevisarCardapio(): boolean {
    const analise = this.analiseCardapio;
    return !!(analise && analise.rascunho) && this.etapa === 'REVISANDO_CARDAPIO' &&
      ['REVISANDO', 'PARCIAL'].indexOf(analise!.status) >= 0 && !this.acaoPendente;
  }

  get analisando(): boolean {
    return this.operandoCardapio && !this.podeRevisarCardapio;
  }

  get referenciasSelecionadas(): string[] {
    const rascunho = this.analiseCardapio && this.analiseCardapio.rascunho;
    if (!rascunho) return [];
    const referencias: string[] = [];
    rascunho.categorias.forEach((categoria: any) =>
      categoria.produtos.forEach((produto: any) => {
        if (produto.selecionado !== false) referencias.push(produto.referencia);
      }));
    return referencias;
  }

  get totalProdutosRascunho(): number {
    const estatisticas = this.analiseCardapio && this.analiseCardapio.estatisticas;
    return Number((estatisticas && estatisticas.produtos) || 0);
  }

  /** produtos bloqueados, não pendências: 26 pizzas sem preço são 26, não 52 */
  get bloqueiosRascunho(): number {
    const analise = this.analiseCardapio;
    if (analise && typeof analise.produtosBloqueados === 'number') return analise.produtosBloqueados;
    const estatisticas = analise && analise.estatisticas;
    return Number((estatisticas && estatisticas.pendenciasBloqueantes) || 0);
  }

  get itensProntosRascunho(): number {
    return Math.max(0, this.totalProdutosRascunho - this.bloqueiosRascunho);
  }

  get tituloDaAcao(): string {
    if (!this.acaoPendente) return '';
    const titulos: {[tipo: string]: string} = {
      CRIAR_EMPRESA: 'Criar empresa',
      IMPORTAR_CARDAPIO: 'Importar cardápio',
      CONFIGURAR_FORMAS_ENTREGA: 'Configurar formas de entrega',
    };
    return titulos[this.acaoPendente.tipo] || this.acaoPendente.tipo;
  }

  /** o rótulo nomeia o efeito real e cabe nos 20 caracteres do WhatsApp */
  get rotuloDaAprovacao(): string {
    if (!this.acaoPendente) return 'Aprovar';
    const rotulos: {[tipo: string]: string} = {
      CRIAR_EMPRESA: 'Aprovar e criar',
      IMPORTAR_CARDAPIO: 'Aprovar e importar',
      CONFIGURAR_FORMAS_ENTREGA: 'Aprovar e salvar',
    };
    return rotulos[this.acaoPendente.tipo] || 'Aprovar';
  }

  get previewEmpresa(): any | null {
    return this.acaoPendente && this.acaoPendente.tipo === 'CRIAR_EMPRESA'
      ? this.acaoPendente.resumo
      : null;
  }

  get dnsTemErro(): boolean {
    return !!this.provisionamentoEmpresa &&
      this.provisionamentoEmpresa.dns.some(item => item.status === 'ERRO');
  }

  get etapaLegivel(): string {
    return String(this.etapa || '').replace(/_/g, ' ').toLowerCase();
  }

  rotuloDaFerramenta(nome: string): string {
    const rotulos: {[nome: string]: string} = {
      obter_estado_onboarding: 'consultou o progresso',
      validar_subdominio: 'verificou o endereço do cardápio',
      propor_criacao_empresa: 'preparou a proposta da empresa',
      obter_estado_cardapio: 'consultou o cardápio',
      analisar_cardapio: 'analisou o cardápio',
      atualizar_precos_cardapio: 'atualizou os preços do cardápio',
      propor_importacao_cardapio: 'preparou a proposta de importação',
    };
    return rotulos[nome] || String(nome || '').replace(/_/g, ' ');
  }

  statusDnsLegivel(status: string): string {
    const rotulos: {[status: string]: string} = {
      PENDENTE: 'propagando',
      CONFIGURADO: 'configurado',
      ERRO: 'requer atenção',
      NAO_APLICAVEL: 'somente em produção',
    };
    return rotulos[status] || status;
  }

  ngOnDestroy(): void {
    this.libereImagens();
  }

  /** só fotos ganham preview; PDF segue como cartão de documento */
  private previewsDe(arquivos: File[]): string[] | undefined {
    const fotos = arquivos.filter(arquivo => /^image\//.test(arquivo.type));
    if (!fotos.length || typeof URL === 'undefined' || !URL.createObjectURL) return undefined;
    const urls = fotos.map(foto => URL.createObjectURL(foto));
    this.urlsDeImagem.push(...urls);
    return urls;
  }

  private libereImagens(): void {
    this.urlsDeImagem.forEach(url => URL.revokeObjectURL(url));
    this.urlsDeImagem = [];
  }

  tamanhoLegivel(bytes: number): string {
    return bytes >= 1024 * 1024
      ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
      : `${Math.max(1, Math.round(bytes / 1024))} KB`;
  }

  private apliqueSnapshot(snapshot: SnapshotMiaGestor): void {
    this.onboardingId = snapshot.id || snapshot.onboardingId || this.onboardingId;
    this.empresaId = snapshot.empresaId;
    this.etapa = snapshot.etapa;
    this.status = snapshot.status;
    this.versao = snapshot.versao;
    this.checklist = snapshot.checklist;
    this.acaoPendente = snapshot.acaoPendente || null;
    if (snapshot.provisionamentoEmpresa !== undefined) {
      this.provisionamentoEmpresa = snapshot.provisionamentoEmpresa || null;
    }
    if (snapshot.analiseCardapio !== undefined) {
      this.analiseCardapio = snapshot.analiseCardapio || null;
    }
    this.erro = '';
  }

  /**
   * Fala da Mia disparada por um evento (etapa concluída): entra como bolha
   * normal, com botões e link. Devolve false quando não houve resposta, para a
   * tela cair no anúncio determinístico.
   */
  private apresenteTurnoDaMia(turno: RespostaTurnoMiaGestor | null | undefined): boolean {
    if (!turno || !turno.texto) return false;
    this.agenteDisponivel = true;
    this.conversaId = turno.conversaId || this.conversaId;
    this.mensagens.push({
      papel: 'gestor',
      texto: turno.texto,
      horario: new Date(),
      interativo: interativoDeResposta(turno.botoes || [], turno.link),
      ferramentas: turno.ferramentas || [],
    });
    return true;
  }

  private papelDe(papel: string): PapelMensagemGestor {
    if (papel === 'usuario' || papel === 'gestor') return papel;
    return 'nota';
  }

  private textoDoErro(erro: any, padrao: string): string {
    return erro && erro.error && erro.error.erro ? erro.error.erro : padrao;
  }

  private valideArquivos(arquivos: File[]): string | null {
    const pdfs = arquivos.filter(arquivo =>
      arquivo.type === 'application/pdf' || arquivo.name.toLowerCase().endsWith('.pdf'));
    if (pdfs.length) {
      if (arquivos.length !== 1) return 'Envie um único PDF ou somente fotos — os dois juntos não dá.';
      if (pdfs[0].size > 10 * 1024 * 1024) return 'O PDF precisa ter no máximo 10 MB.';
      return null;
    }
    if (arquivos.length > 10) return 'São no máximo 10 fotos por análise.';
    const invalida = arquivos.find(arquivo =>
      ['image/jpeg', 'image/png', 'image/webp'].indexOf(arquivo.type) < 0 ||
      arquivo.size > 5 * 1024 * 1024);
    return invalida ? 'Use fotos JPG, PNG ou WebP de até 5 MB cada.' : null;
  }
}
